import LandingHeadline from "../common/LandingHeadline";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  KeyRound,
  UserCheck,
  Cpu,
  Link2,
  Activity,
  LineChart,
  Lock,
  FileBarChart,
  ArrowRight,
  Search,
  X,
  Plus,
  ArrowLeftRight,
  BadgeCheck,
} from "lucide-react";
import Header from "../Header/Header";
import Footer from "../Footer/footer";
import SEOHead from "../SEO/SEOHead";
import { SectionHeading, Button, BreadcrumbSchema } from "../common/ui";
import { integrations, IntegrationLogo } from "../common/integrations";
import "../../css/MainPage.css";
import "../../css/Pages.css";
import "../../css/Integration.css";
import FrothAiIcon from "../common/FrothAiIcon";
import McpConnect, { assistants } from "../common/McpConnect";
import Watermark from "../common/Watermark";
import frothLogo from "../../../assets/logo.svg";

// ---------- Directory data ----------
const categories = [
  { id: "all", label: "All" },
  { id: "work", label: "Work items" },
  { id: "code", label: "Code & CI" },
  { id: "devices", label: "Devices & browsers" },
  { id: "ai", label: "AI assistants" },
];

// Category and capability chips for each tool in common/integrations.
const toolMeta = {
  Jira: { category: "work", caps: ["1- or 2-way sync", "Field mapping", "User mapping"] },
  "Azure DevOps": { category: "work", caps: ["2-way sync", "Field mapping", "User mapping"] },
  Plane: { category: "work", caps: ["1- or 2-way sync", "Requirements", "Defects"] },
  GitHub: { category: "code", caps: ["Actions runners", "Script push", "Code scanning"] },
  GitLab: { category: "code", caps: ["GitLab Runner", "Vulnerabilities", "Leaked secrets"] },
  BrowserStack: { category: "devices", caps: ["Real devices", "Cross-browser", "Capability generator"] },
};

const catalog = [
  ...integrations.map(item => ({ ...item, kind: "tool", ...(toolMeta[item.name] || { category: "work", caps: [] }) })),
  ...assistants.map(a => ({
    kind: "ai",
    name: a.name,
    Icon: a.Icon,
    color: a.color,
    verified: a.verified,
    category: "ai",
    caps: ["Remote MCP", "OAuth 2.0", "Read-only"],
    text: `Ask ${a.name} about your Froth TestOps QA data over MCP, read-only and with your own permissions.`,
  })),
];

// ---------- Sync demo data ----------
const syncTargets = [
  { name: "Jira", id: "QA-318", status: "In Progress" },
  { name: "Azure DevOps", id: "Bug 4127", status: "Active" },
  { name: "Plane", id: "WEB-92", status: "In Progress" },
];

const syncFields = [
  { label: "Title", froth: "Checkout fails on Safari 17", map: "Field" },
  { label: "Status", froth: "In Progress", map: "Status" },
  { label: "Priority", froth: "High", map: "Field" },
  { label: "Assignee", froth: "A. Rao", map: "User" },
  { label: "Environment", froth: "Staging", map: "Custom" },
];

// ---------- Sections below the fold ----------
const signIn = [
  {
    icon: UserCheck,
    title: "Azure AD single sign-on",
    text: "Sign in with your organisation's Microsoft Azure AD account, or with a username and password.",
  },
  {
    icon: KeyRound,
    title: "Personal API tokens",
    text: "Per-user tokens with 30, 60 or 90-day expiry keep API and automation access secure.",
  },
  {
    icon: Lock,
    title: "Role-based access",
    text: "A permission matrix for every module and action, with changes applied instantly.",
  },
];

const execution = [
  {
    icon: Cpu,
    title: "Capability Generator",
    text: "Generate BrowserStack device and browser capabilities in a few clicks.",
  },
  {
    icon: Link2,
    title: "Project & field mapping",
    text: "Map projects, fields and users between Froth TestOps and your tools.",
  },
  {
    icon: Activity,
    title: "Live execution & logs",
    text: "Watch executions live and keep logs, reports and defects together.",
  },
];

const why = [
  {
    icon: FrothAiIcon,
    tag: "FrothAI",
    title: "AI-Powered Suggestions",
    text: "Use smart suggestions to create tests quickly and catch issues early.",
  },
  {
    icon: LineChart,
    tag: "Dashboards",
    title: "Real-Time Analytics",
    text: "Detailed dashboards, execution trends and test coverage metrics provide actionable insights.",
  },
  {
    icon: Lock,
    tag: "Role-based",
    title: "Secure Collaboration",
    text: "Team members share test progress securely with role-based access control.",
  },
  {
    icon: FileBarChart,
    tag: "Scheduled",
    title: "Scalable Reporting",
    text: "Build reports, compare cycles side by side and schedule reports to your team by email.",
  },
];

function CatalogLogo({ item }) {
  if (item.kind === "ai") {
    const { Icon, color } = item;
    return (
      <span style={{ color }}>
        <Icon aria-hidden="true" />
      </span>
    );
  }
  return <IntegrationLogo item={item} />;
}

// Hero visual: an integrations console. A settings window lists every tool with what it
// connects and a toggle that switches on, and an MCP card overlaps it with an AI assistant
// answering from real FrothShop demo data (FSHOP-DT-6 is the Blocker, FSHOP-DT-4 Critical).
const CONNECTS = {
  Jira: "Requirements · Defects",
  "Azure DevOps": "Work items",
  Plane: "Requirements · Defects",
  GitHub: "Actions runners · Code scans",
  GitLab: "GitLab Runner · Code scans",
  BrowserStack: "Real devices & browsers",
};
const TWO_WAY = ["Jira", "Azure DevOps", "Plane"];

function IntegrationConsole() {
  return (
    <div
      className="int-console"
      role="img"
      aria-label={`Froth TestOps connects with ${integrations.map(i => i.name).join(", ")}, and with ${assistants
        .map(a => a.name)
        .join(", ")} over MCP`}
    >
      <div className="int-console__win" aria-hidden="true">
        <div className="int-console__bar">
          <span className="int-console__dots">
            <i />
            <i />
            <i />
          </span>
          <span className="int-console__path">
            Settings <span>/</span> <strong>Integrations</strong>
          </span>
          <img src={frothLogo} alt="" className="int-console__brand" width="64" height="24" />
        </div>
        <ul className="int-console__list">
          {integrations.map((item, i) => (
            <li className="int-console__row" key={item.name} style={{ "--i": i }}>
              <span className="int-console__logo">
                <IntegrationLogo item={item} />
              </span>
              <span className="int-console__meta">
                <strong>{item.name}</strong>
                <small>{CONNECTS[item.name]}</small>
              </span>
              {TWO_WAY.includes(item.name) && (
                <span className="int-console__sync">
                  <ArrowLeftRight size={11} /> Two-way
                </span>
              )}
              <span className="int-console__toggle" />
            </li>
          ))}
        </ul>
      </div>

      <div className="int-console__mcp" aria-hidden="true">
        <div className="int-console__mcp-head">
          <span>AI assistants · MCP</span>
          <span className="int-console__assistants">
            {assistants.map(({ name, Icon, color }, i) => (
              <span key={name} className={i === 0 ? "is-on" : undefined} style={{ color }}>
                <Icon />
              </span>
            ))}
          </span>
        </div>
        <p className="int-console__ask">Which defects are blocking the FrothShop release?</p>
        <div className="int-console__answer">
          <span>Two high-severity defects are still open:</span>
          <span className="int-console__defect">
            <b className="is-blocker">Blocker</b> <code>FSHOP-DT-6</code> <em>UPI payment stays pending</em>
          </span>
          <span className="int-console__defect">
            <b className="is-critical">Critical</b> <code>FSHOP-DT-4</code> <em>Expired coupon still applies</em>
          </span>
        </div>
        <span className="int-console__foot">
          <Lock size={12} /> Read-only · your permissions
        </span>
      </div>
    </div>
  );
}

// Searchable, filterable directory of every integration.
function Directory() {
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");

  const counts = useMemo(
    () =>
      Object.fromEntries(
        categories.map(c => [c.id, c.id === "all" ? catalog.length : catalog.filter(i => i.category === c.id).length])
      ),
    []
  );

  const q = query.trim().toLowerCase();
  const shown = catalog.filter(
    item =>
      (category === "all" || item.category === category) &&
      (!q || `${item.name} ${item.text} ${item.caps.join(" ")}`.toLowerCase().includes(q))
  );

  // Cursor-following glow on each card.
  const spotlight = e => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--x", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--y", `${e.clientY - r.top}px`);
  };

  return (
    <section className="ft-section int-dir" id="directory">
      <div className="ft-container">
        <SectionHeading
          eyebrow="Directory"
          title={
            <>
              Every integration, <span>one place</span>
            </>
          }
          subtitle="Requirement and defect sync, CI-driven automation, real-device testing, code scanning and AI assistants, all set up from Settings."
        />

        <div className="int-dir__bar">
          <div className="int-dir__tabs" role="tablist" aria-label="Filter integrations">
            {categories.map(c => (
              <button
                key={c.id}
                type="button"
                role="tab"
                aria-selected={category === c.id}
                className={`int-dir__tab${category === c.id ? " is-active" : ""}`}
                onClick={() => setCategory(c.id)}
              >
                {c.label}
                <span className="int-dir__count">{counts[c.id]}</span>
              </button>
            ))}
          </div>
          <label className="int-dir__search">
            <Search size={16} aria-hidden="true" />
            <input
              type="search"
              placeholder="Search integrations"
              value={query}
              onChange={e => setQuery(e.target.value)}
              aria-label="Search integrations"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} aria-label="Clear search">
                <X size={14} aria-hidden="true" />
              </button>
            )}
          </label>
        </div>

        <ul className="int-dir__grid" aria-live="polite">
          {shown.map((item, i) => (
            <li className="int-card" key={item.name} onPointerMove={spotlight}>
              <Watermark className="int-card__mark" delay={(i % 3) * 90}>
                <CatalogLogo item={item} />
              </Watermark>
              {(item.kind !== "ai" || item.verified) && (
                <div className="int-card__top">
                  {item.kind === "ai" ? (
                    <span className="int-card__status is-verified">
                      <BadgeCheck size={13} aria-hidden="true" /> Verified
                    </span>
                  ) : (
                    <span className="int-card__status">
                      <i aria-hidden="true" /> Available
                    </span>
                  )}
                </div>
              )}
              <h3>{item.name}</h3>
              <span className="int-card__cat">{categories.find(c => c.id === item.category).label}</span>
              <p>{item.text}</p>
              <ul className="int-card__caps">
                {item.caps.map(cap => (
                  <li key={cap}>{cap}</li>
                ))}
              </ul>
              {item.kind === "ai" && (
                <a className="int-card__link" href="#ai-assistants">
                  Connect {item.name} <ArrowRight size={14} aria-hidden="true" />
                </a>
              )}
            </li>
          ))}

          <li className="int-card int-card--request">
            <span className="int-card__plus">
              <Plus size={20} aria-hidden="true" />
            </span>
            <h3>{shown.length ? "Need another tool?" : `No match for "${query}"`}</h3>
            <p>Tell us what your team uses and we'll talk through how it fits with Froth TestOps.</p>
            <Link className="int-card__link" to="/contact">
              Request an integration <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </li>
        </ul>
      </div>
    </section>
  );
}

// Illustration of a defect syncing two ways with the chosen tracker.
function SyncDemo() {
  const [active, setActive] = useState(0);
  const target = syncTargets[active];
  const targetItem = integrations.find(i => i.name === target.name);

  return (
    <section className="ft-section ft-section--alt int-sync">
      <div className="ft-container">
        <SectionHeading
          align="left"
          eyebrow="Sync in action"
          title={
            <>
              Defects stay in step, <span>both ways</span>
            </>
          }
          subtitle="Map fields, statuses and users once. Changes made on either side flow across, so testers and developers work from the same record."
        />

        <div className="int-sync__board">
          <article className="int-sync__card">
            <header>
              <img src={frothLogo} alt="Froth TestOps" width="88" height="32" />
              <span className="int-sync__id">DEF-142</span>
            </header>
            <dl>
              {syncFields.map(f => (
                <div key={f.label}>
                  <dt>{f.label}</dt>
                  <dd>{f.froth}</dd>
                </div>
              ))}
            </dl>
          </article>

          <div className="int-sync__rail" aria-hidden="true">
            <span className="int-sync__badge">
              <ArrowLeftRight size={14} /> Two-way sync
            </span>
            {syncFields.map((f, i) => (
              <div className="int-sync__lane" key={f.label} style={{ "--d": `${i * 0.35}s` }}>
                <span className="int-sync__map">{f.map} mapping</span>
              </div>
            ))}
          </div>

          <article className="int-sync__card" key={target.name}>
            <header>
              <span className="int-sync__tool">
                <IntegrationLogo item={targetItem} />
                {target.name}
              </span>
              <span className="int-sync__id">{target.id}</span>
            </header>
            <dl>
              {syncFields.map(f => (
                <div key={f.label}>
                  <dt>{f.label}</dt>
                  <dd>{f.label === "Status" ? target.status : f.froth}</dd>
                </div>
              ))}
            </dl>
            <footer>
              <span className="int-sync__live" /> Synced just now
            </footer>
          </article>
        </div>

        <div className="int-sync__pick" role="tablist" aria-label="Sync with">
          {syncTargets.map((t, i) => (
            <button
              key={t.name}
              type="button"
              role="tab"
              aria-selected={i === active}
              className={i === active ? "is-active" : ""}
              onClick={() => setActive(i)}
            >
              <IntegrationLogo item={integrations.find(x => x.name === t.name)} />
              {t.name}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

const Integration = () => {
  return (
    <>
      <SEOHead
        title="Best AI Testing Tools to Enhance and Boost Software QA | Integrations"
        description="Connect Froth TestOps with Jira, Azure DevOps, Plane, GitHub, GitLab and BrowserStack, and give Claude, ChatGPT, Gemini or GitHub Copilot read-only access to your QA data over MCP."
        keywords="best AI testing tools, test management integrations, MCP, Claude, ChatGPT, Gemini, GitHub Copilot, Jira, Azure DevOps, GitHub, GitLab, BrowserStack, Froth TestOps"
      />
      <div className="fd-main-wrapper page-padding">
        <Header />
        <main className="int-page">
          <section className="int-hero">
            <BreadcrumbSchema name="Integrations" />
            <div className="ft-container int-hero__grid">
              <div className="int-hero__copy">
                <nav className="ft-breadcrumb" aria-label="Breadcrumb">
                  <Link to="/">Home</Link>
                  <span aria-hidden="true">/</span>
                  <span aria-current="page">Integrations</span>
                </nav>
                <span className="int-hero__pill">
                  <span>New</span> MCP connectors for AI assistants
                </span>
                <LandingHeadline className="ft-display">
                  Seamless integrations for <span>smarter testing</span>
                </LandingHeadline>
                <p className="int-hero__lead">
                  Connect Froth TestOps to the tools your team already uses, and let your AI assistant read your QA
                  data over MCP.
                </p>
                <div className="ft-btn-row">
                  <Button to="/contact" icon>
                    Talk to Us
                  </Button>
                  <a href="#directory" className="ft-btn int-hero__ghost">
                    Browse integrations
                  </a>
                </div>
                <ul className="int-hero__stats">
                  <li>
                    <strong>{integrations.length}</strong> tool integrations
                  </li>
                  <li>
                    <strong>{assistants.length}</strong> AI assistants
                  </li>
                  <li>
                    <strong>2-way</strong> work-item sync
                  </li>
                </ul>
              </div>
              <IntegrationConsole />
            </div>
          </section>

          <Directory />

          <SyncDemo />

          <McpConnect />

          <section className="ft-section">
            <div className="ft-container">
              <SectionHeading
                align="left"
                eyebrow="Built in"
                title={
                  <>
                    Secure sign-in, <span>seamless execution</span>
                  </>
                }
                subtitle="Enterprise sign-in and fine-grained access control, with the tooling to run tests across every integration."
              />
              <div className="int-pair">
                {[
                  { label: "Sign-in & access", items: signIn },
                  { label: "Test execution", items: execution },
                ].map(col => (
                  <div className="int-pair__col" key={col.label}>
                    <span className="ft-label">{col.label}</span>
                    <ul>
                      {col.items.map(({ icon: Icon, title, text }) => (
                        <li key={title}>
                          <span className="int-pair__icon">
                            <Icon size={20} aria-hidden="true" />
                          </span>
                          <div>
                            <h3>{title}</h3>
                            <p>{text}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="ft-section ft-section--alt">
            <div className="ft-container int-why">
              <div className="int-why__intro">
                <span className="ft-eyebrow">Why Froth TestOps</span>
                <h2 className="ft-heading">
                  Why choose <span>Froth TestOps</span>
                </h2>
                <p className="ft-lead">
                  Intelligent tools, seamless integrations and real-time collaboration give your QA team speed,
                  visibility and control across the whole testing lifecycle, without adding complexity.
                </p>
                <Link to="/why-testops" className="ft-more">
                  See why teams switch <ArrowRight size={16} aria-hidden="true" />
                </Link>
              </div>
              <ol className="int-why__grid">
                {why.map(({ icon: Icon, tag, title, text }, i) => (
                  <li className="int-why__item" key={title}>
                    <div className="int-why__top">
                      <span className="int-why__icon">
                        <Icon size={22} aria-hidden="true" />
                      </span>
                      <span className="int-why__num">0{i + 1}</span>
                    </div>
                    <h3>{title}</h3>
                    <p>{text}</p>
                    <span className="int-why__tag">{tag}</span>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Integration;
